
import React, { useState, useRef, useEffect } from 'react';
import { registrationSchema, RegistrationFormValues } from '@/schemas/registrationSchema';
import RegistrationQRCode from '../RegistrationQRCode';
import { useFormWithPersistence } from '@/hooks/use-form-persistence';
import { FormHeader } from './';
import { FormContainer } from './';
import RegistrationProgress from './RegistrationProgress';
import FormContent from './FormContent';
import { useFormHandlers } from './handlers/FormHandlers';
import { removeAccents } from '../registration-qr/utils';

const RegistrationForm = () => {
  const [selectedClassYear, setSelectedClassYear] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState<RegistrationFormValues | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // Use hook with localStorage persistence
  const formMethods = useFormWithPersistence<RegistrationFormValues>(
    registrationSchema,
    'registration-form',
    {
      fullName: '',
      email: '',
      phone: '',
      class: '12A1', // Default to first class instead of empty string to avoid type error
      additionalContribution: 500000,
      t_shirt_size: 'Nam_L', // Default T-shirt size with prefix
      gender: 'male' // Default gender
    }
  );
  
  const { 
    handleSubmit: hookFormSubmit, 
    setError, 
    setFocus, 
    reset,
    formState: { errors },
    isRestoringData,
    getValues,
    setValue 
  } = formMethods;

  // Handle contribution amount value sync from form to state
  useEffect(() => {
    // Add debugging to track contribution amounts
    if (!isSubmitted && formRef.current) {
      const currentValues = getValues();
      console.log("Current form contribution value:", currentValues.additionalContribution);
    }
  }, [getValues, isSubmitted]);
  
  const handleFormSuccess = (data: RegistrationFormValues) => {
    // Process the data - normalize name to remove accents for QR code generation
    const processedData = {
      ...data,
      // We keep the original fullName for display but log the normalized version
      _normalizedName: removeAccents(data.fullName)
    };
    
    // Ensure additionalContribution is a number
    const additionalContribution = typeof data.additionalContribution === 'number' 
      ? data.additionalContribution 
      : parseInt(data.additionalContribution as unknown as string) || 0;
    
    // Create the final form data object with validated contribution
    const finalFormData = {
      ...data,
      additionalContribution: additionalContribution
    };
    
    console.log("Form data processed:", {
      original: data.fullName,
      normalized: processedData._normalizedName,
      additionalContribution: finalFormData.additionalContribution
    });
    
    // Make sure we have the latest contribution amount
    setFormData(finalFormData);
    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  // Use extracted form handlers
  const { handleFormSubmit, handleValidationError } = useFormHandlers({
    onSubmitSuccess: handleFormSuccess,
    setIsSubmitting,
    setFocus,
    setError
  });

  // Handler for going back from QR screen to form
  const handleGoBack = () => {
    setIsSubmitted(false);
    
    // The form data is preserved since we don't reset the form state
    // We can pre-fill the form again if needed
    if (formData) {
      // Set the class year for the image display
      setSelectedClassYear(formData.class);
      
      // Reset all form values with the formData to ensure the dropdown gets populated
      reset(formData);
    }
  };

  // Handle year change for image display
  const handleYearChange = (year: string | null) => {
    setSelectedClassYear(year);
  };

  // Wrap the form submission to properly handle the event
  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Ensure we have the latest additionalContribution value from the form
    const formValues = getValues();
    
    // Make sure contribution is a number
    let contribution = formValues.additionalContribution;
    if (typeof contribution === 'string') {
      // Fix: Check if contribution is a string before using replace
      contribution = parseInt(String(contribution).replace(/\D/g, '')) || 0;
      setValue('additionalContribution', contribution);
    }
    
    console.log('Submitting form with contribution:', contribution);
    
    hookFormSubmit(
      (data) => handleFormSubmit(data), 
      (errors) => handleValidationError(errors, formRef)
    )();
  };

  if (isSubmitted && formData) {
    return <RegistrationQRCode formData={formData} onGoBack={handleGoBack} />;
  }

  return (
    <FormContainer>
      {/* Thanh tiến trình */}
      <RegistrationProgress currentStep={1} />
      
      {/* Form header với animation */}
      <FormHeader />
      
      {/* Form content */}
      <FormContent 
        formRef={formRef}
        formMethods={formMethods}
        onSubmit={onSubmit}
        onYearChange={handleYearChange}
        isSubmitting={isSubmitting}
        isRestoringData={isRestoringData}
      />
    </FormContainer>
  );
};

export default RegistrationForm;
